/* ============================================================
   VALOTERNA — Warenkorb
   Datei: assets/valoterna-cart.js
   Eigenständig; nutzt nur window.routes (theme.liquid) und,
   falls vorhanden, publish/PUB_SUB_EVENTS aus Dawn.
   ============================================================ */
(() => {
  if (customElements.get('vl-cart')) return;

  const RM = window.matchMedia('(prefers-reduced-motion: reduce)');
  const ms = (n) => (RM.matches ? 0 : n);
  const wait = (n) => new Promise((r) => setTimeout(r, n));
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const UNDO_MS = 7000;

  /* ---------- Zähler für die Gesamtsumme ---------- */
  class Odometer {
    constructor(root) {
      this.root = root;
      this.sr = root.querySelector('[data-vl-total-sr]');
      this.face = root.querySelector('[data-vl-total-face]');
      this.value = root.dataset.value || this.face.textContent.trim();
    }

    set(next) {
      if (!next || next === this.value) return false;
      const prev = this.value;
      this.value = next;
      this.root.dataset.value = next;
      this.sr.textContent = next;

      if (RM.matches) {
        this.face.textContent = next;
        return true;
      }

      const n = [...next];
      const p = [...prev];
      const offset = p.length - n.length; // rechtsbündig vergleichen
      const frag = document.createDocumentFragment();
      const strips = [];

      n.forEach((ch, i) => {
        if (!/\d/.test(ch)) {
          const s = document.createElement('span');
          s.className = 'vl-odo__ch';
          s.textContent = ch;
          frag.appendChild(s);
          return;
        }
        const col = document.createElement('span');
        col.className = 'vl-odo__col';
        const strip = document.createElement('span');
        strip.className = 'vl-odo__strip';
        for (let d = 0; d < 10; d++) {
          const s = document.createElement('span');
          s.textContent = d;
          strip.appendChild(s);
        }
        const pc = p[i + offset];
        const hadDigit = /\d/.test(pc || '');
        const from = hadDigit ? +pc : +ch;
        if (!hadDigit) col.classList.add('is-new');
        strip.style.transform = `translateY(${-from * 10}%)`;
        strip.style.transitionDelay = `${(n.length - 1 - i) * 35}ms`; // rollt von rechts nach links
        strip.dataset.to = ch;
        col.appendChild(strip);
        frag.appendChild(col);
        strips.push(strip);
      });

      this.face.replaceChildren(frag);
      requestAnimationFrame(() =>
        requestAnimationFrame(() => strips.forEach((s) => (s.style.transform = `translateY(${-s.dataset.to * 10}%)`)))
      );
      clearTimeout(this.cleanup);
      this.cleanup = setTimeout(() => {
        if (this.value === next) this.face.textContent = next;
      }, 1500);
      return true;
    }
  }

  /* ---------- Warenkorb ---------- */
  class VlCart extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.sectionId = this.dataset.sectionId;
      this.locale = this.dataset.locale || document.documentElement.lang || 'de';
      try {
        this.strings = JSON.parse(this.querySelector('[data-vl-strings]').textContent);
      } catch (e) {
        this.strings = {};
      }
      this.queue = Promise.resolve();
      this.pending = 0;
      this.undoSnapshot = null;

      this.toast = this.querySelector('[data-vl-toast]');
      this.toast?.style.setProperty('--vl-undo-ms', `${UNDO_MS}ms`);
      this.toast?.querySelector('[data-vl-toast-timer]')?.addEventListener('animationend', () => this.hideToast());

      this.bindEvents();
      this.initBody();

      setTimeout(() => this.classList.remove('is-intro'), 1600);

      window.addEventListener('pageshow', (e) => {
        if (e.persisted) this.resetCheckout();
      });
    }

    disconnectedCallback() {
      this.io?.disconnect();
    }

    t(key, vars = {}) {
      let s = this.strings[key] || '';
      Object.entries(vars).forEach(([k, v]) => (s = s.split(`[${k}]`).join(v)));
      return s;
    }

    /* Alles, was zum (austauschbaren) Body gehört */
    initBody() {
      const total = this.querySelector('[data-vl-total]');
      this.odo = total ? new Odometer(total) : null;
      this.initEta();
      this.initBar();
    }

    /* ---------- Events (delegiert, überleben jeden Austausch) ---------- */
    bindEvents() {
      // Druck & Loslassen
      this.addEventListener('pointerdown', (e) => {
        const b = e.target.closest('[data-vl-press]');
        if (!b || e.button > 0) return;
        const r = b.getBoundingClientRect();
        b.style.setProperty('--px', `${e.clientX - r.left}px`);
        b.style.setProperty('--py', `${e.clientY - r.top}px`);
        b.classList.add('is-pressed');
        this.pressed = b;
      });
      const release = () => {
        this.pressed?.classList.remove('is-pressed');
        this.pressed = null;
      };
      document.addEventListener('pointerup', release);
      document.addEventListener('pointercancel', release);
      this.addEventListener('pointerout', (e) => {
        // Finger/Maus zieht weg, ohne loszulassen → sanft zurück, keine Aktion
        if (this.pressed && !this.pressed.contains(e.relatedTarget)) release();
      });

      this.addEventListener('click', (e) => {
        const t = e.target;

        const rm = t.closest('[data-vl-remove]');
        if (rm) {
          e.preventDefault();
          this.remove(rm.closest('[data-vl-item]'), rm);
          return;
        }
        if (t.closest('[data-vl-undo]')) return this.undo();

        const sw = t.closest('[data-vl-gift-toggle]');
        if (sw) return this.toggleGift(sw);

        if (t.closest('[data-vl-reserve-open]')) return this.openReserve();
        if (t.closest('[data-vl-reserve-close]') || t.matches('dialog[data-vl-reserve]')) return this.closeReserve();
        if (t.closest('[data-vl-res-retry]')) return this.resState('form');

        const co = t.closest('[data-vl-checkout]');
        if (co) this.onCheckout(e, co);
      });

      this.addEventListener('input', (e) => {
        if (e.target.matches('[data-vl-gift-note]')) this.onNoteInput(e.target);
      });

      this.addEventListener('submit', (e) => {
        if (e.target.matches('[data-vl-reserve-form]')) {
          e.preventDefault();
          this.submitReserve(e.target);
        }
      });

      this.addEventListener(
        'cancel',
        (e) => {
          if (e.target.matches('dialog[data-vl-reserve]')) {
            e.preventDefault();
            this.closeReserve();
          }
        },
        true
      );
    }

    /* ---------- Netzwerk ---------- */
    url(kind) {
      const r = window.routes || {};
      // immer die .js-Endpunkte: liefern garantiert JSON statt einer Weiterleitung
      const base = { change: r.cart_change_url || '/cart/change', add: r.cart_add_url || '/cart/add', update: r.cart_update_url || '/cart/update' }[kind];
      return base.endsWith('.js') ? base : `${base}.js`;
    }

    sectionsPayload() {
      const list = [this.sectionId];
      if (document.getElementById('cart-icon-bubble')) list.push('cart-icon-bubble');
      return { sections: list, sections_url: window.location.pathname };
    }

    async post(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status >= 400 || (data.errors && !data.sections)) {
        const err = new Error(data.description || data.message || 'Request failed');
        err.data = data;
        throw err;
      }
      return data;
    }

    // Anfragen nacheinander, damit sich Zustände nie überholen
    enqueue(fn) {
      this.pending++;
      const p = this.queue.then(fn);
      this.queue = p.catch(() => {}).then(() => this.pending--);
      return p;
    }

    docFrom(data) {
      const html = data.sections?.[this.sectionId];
      return html ? parse(html) : null;
    }

    /* ---------- DOM-Abgleich ---------- */
    swapRegion(doc, name) {
      const src = doc?.querySelector(`[data-vl-swap="${name}"]`);
      const dst = this.querySelector(`[data-vl-swap="${name}"]`);
      if (!src || !dst) return null;
      dst.replaceWith(src);
      return src;
    }

    applyDoc(doc) {
      if (!doc) return;
      const oldCount = this.querySelector('[data-vl-swap="count"]')?.textContent.trim();
      const count = this.swapRegion(doc, 'count');
      if (count && count.textContent.trim() !== oldCount) count.classList.add('is-changed');

      this.swapRegion(doc, 'rows');

      const next = doc.querySelector('[data-vl-total]')?.dataset.value;
      if (next && this.odo?.set(next)) {
        const wrap = this.querySelector('[data-vl-total-wrap]');
        wrap?.classList.remove('is-updated');
        void wrap?.offsetWidth;
        wrap?.classList.add('is-updated');
        this.announce(this.t('total', { total: next }));
      }
      const bar = this.querySelector('[data-vl-bar-total]');
      if (bar && next) bar.textContent = next;
    }

    async swapBody(doc) {
      const src = doc?.querySelector('[data-vl-body]');
      const dst = this.querySelector('[data-vl-body]');
      if (!src || !dst) return window.location.reload();

      dst.classList.add('is-swapping-out');
      await wait(ms(300));
      this.io?.disconnect();
      src.classList.add('is-swapping-in');
      dst.replaceWith(src);
      this.applyDoc(doc);
      this.initBody();
      setTimeout(() => src.classList.remove('is-swapping-in'), ms(800));
    }

    updateBubble(data) {
      const html = data.sections?.['cart-icon-bubble'];
      const el = document.getElementById('cart-icon-bubble');
      if (!html || !el) return;
      const src = parse(html).querySelector('.shopify-section');
      const target = el.querySelector('.shopify-section') || el;
      if (src) target.innerHTML = src.innerHTML;
    }

    emit(data) {
      try {
        if (typeof window.publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          window.publish(PUB_SUB_EVENTS.cartUpdate, { source: 'valoterna-cart', cartData: data });
        }
      } catch (e) {
        /* optional */
      }
    }

    announce(msg) {
      const live = this.querySelector('[data-vl-live]');
      if (!live || !msg) return;
      live.textContent = '';
      setTimeout(() => (live.textContent = msg), 60);
    }

    /* ---------- Höhe animieren ---------- */
    collapse(el) {
      const cs = getComputedStyle(el);
      el.style.overflow = 'hidden';
      return el
        .animate(
          [
            { height: `${el.offsetHeight}px`, paddingTop: cs.paddingTop, paddingBottom: cs.paddingBottom, borderBottomWidth: cs.borderBottomWidth, opacity: 1 },
            { height: '0px', paddingTop: '0px', paddingBottom: '0px', borderBottomWidth: '0px', opacity: 0 },
          ],
          { duration: ms(480), easing: 'cubic-bezier(.65,0,.35,1)', fill: 'forwards' }
        )
        .finished.catch(() => {});
    }

    expand(el) {
      const cs = getComputedStyle(el);
      const h = el.offsetHeight;
      el.style.overflow = 'hidden';
      return el
        .animate(
          [
            { height: '0px', paddingTop: '0px', paddingBottom: '0px', opacity: 0 },
            { height: `${h}px`, paddingTop: cs.paddingTop, paddingBottom: cs.paddingBottom, opacity: 1 },
          ],
          { duration: ms(560), easing: 'cubic-bezier(.16,1,.3,1)' }
        )
        .finished.catch(() => {})
        .then(() => (el.style.overflow = ''));
    }

    /* ---------- Entfernen ---------- */
    snapshot(item) {
      let properties = {};
      try {
        const raw = JSON.parse(item.dataset.properties || '{}') || {};
        Object.entries(raw).forEach(([k, v]) => {
          if (v !== null && v !== undefined && String(v).trim() !== '') properties[k] = String(v);
        });
      } catch (e) {
        /* leer */
      }
      return {
        variantId: Number(item.dataset.variantId),
        quantity: Number(item.dataset.quantity) || 1,
        properties,
        title: item.dataset.title,
      };
    }

    async remove(item, trigger) {
      if (!item || item.dataset.state) return;
      item.dataset.state = 'removing';
      const viaKeyboard = trigger?.matches(':focus-visible');
      const snap = this.snapshot(item);
      const errorEl = item.querySelector('[data-vl-item-error]');
      if (errorEl) errorEl.textContent = '';

      // sofortiges Feedback: Linie läuft durch, Stein verblasst
      item.classList.add('is-leaving');

      let data;
      try {
        [data] = await Promise.all([
          this.enqueue(() => this.post(this.url('change'), { id: item.dataset.key, quantity: 0, ...this.sectionsPayload() })),
          wait(ms(320)),
        ]);
      } catch (err) {
        item.classList.remove('is-leaving');
        delete item.dataset.state;
        if (errorEl) errorEl.textContent = this.t('error');
        return;
      }

      const doc = this.docFrom(data);
      if (data.item_count === 0) {
        await this.collapse(item);
        await this.swapBody(doc);
      } else {
        const neighbour = item.nextElementSibling || item.previousElementSibling;
        await this.collapse(item);
        item.remove();
        this.applyDoc(doc);
        if (viaKeyboard) neighbour?.querySelector('[data-vl-remove]')?.focus({ preventScroll: true });
      }

      this.updateBubble(data);
      this.emit(data);
      this.showToast(this.t('removed', { title: snap.title }), true);
      this.undoSnapshot = snap;
      this.announce(this.t('removed', { title: snap.title }));
    }

    async undo() {
      const snap = this.undoSnapshot;
      if (!snap) return;
      this.undoSnapshot = null;
      this.hideToast();

      try {
        const line = { id: snap.variantId, quantity: snap.quantity };
        if (Object.keys(snap.properties).length) line.properties = snap.properties;
        const data = await this.enqueue(() =>
          this.post(this.url('add'), { items: [line], ...this.sectionsPayload() })
        );
        const doc = this.docFrom(data);

        if (!this.querySelector('[data-vl-list]')) {
          await this.swapBody(doc);
        } else {
          const list = this.swapRegion(doc, 'list');
          this.applyDoc(doc);
          const back = list?.querySelector(`[data-vl-item][data-variant-id="${snap.variantId}"]`);
          if (back) {
            back.classList.add('is-restored');
            this.expand(back);
          }
        }
        this.updateBubble(data);
        this.emit(data);
        this.announce(this.t('restored', { title: snap.title }));
      } catch (err) {
        // echten Grund von Shopify zeigen statt einer Vermutung
        console.warn('[VALOTERNA] Rückgängig fehlgeschlagen:', err?.data || err);
        const reason = err?.data?.description || err?.data?.message;
        this.showToast(reason || this.t('error'), false);
      }
    }

    /* ---------- Hinweis unten ---------- */
    showToast(text, withUndo) {
      if (!this.toast) return;
      this.toast.querySelector('[data-vl-toast-text]').textContent = text;
      const btn = this.toast.querySelector('[data-vl-undo]');
      if (btn) btn.hidden = !withUndo;
      this.toast.classList.remove('is-visible');
      void this.toast.offsetWidth; // Zeitleiste neu starten
      this.toast.classList.add('is-visible');
      this.toast.inert = false;
      document.documentElement.classList.add('vl-ui-toast');
    }

    hideToast() {
      if (!this.toast) return;
      if (this.toast.contains(document.activeElement)) document.activeElement.blur();
      this.toast.classList.remove('is-visible');
      this.toast.inert = true;
      document.documentElement.classList.remove('vl-ui-toast');
      this.undoSnapshot = null;
    }

    /* ---------- Geschenk ---------- */
    async toggleGift(sw) {
      const on = sw.getAttribute('aria-checked') !== 'true';
      const panel = this.querySelector('[data-vl-gift-panel]');
      const note = this.querySelector('[data-vl-gift-note]');
      const hidden = this.querySelector('[data-vl-gift-input]');
      const yes = this.strings.yes || 'Ja';

      const apply = (state) => {
        sw.setAttribute('aria-checked', String(state));
        panel?.classList.toggle('is-open', state);
        if (panel) panel.inert = !state;
        if (note) note.disabled = !state;
        if (hidden) hidden.value = state ? yes : '';
      };
      apply(on);
      if (on && note) setTimeout(() => note.focus({ preventScroll: true }), ms(350));

      const attributes = {
        [this.strings.attr_wrap]: on ? yes : '',
        [this.strings.attr_note]: on && note ? note.value.trim() : '',
      };
      try {
        await this.enqueue(() => this.post(this.url('update'), { attributes }));
      } catch (err) {
        apply(!on);
        this.showToast(this.t('error'), false);
      }
    }

    onNoteInput(note) {
      const counter = this.querySelector('[data-vl-counter]');
      if (counter) counter.textContent = `${note.value.length}/${note.maxLength}`;
      clearTimeout(this.noteTimer);
      this.noteTimer = setTimeout(async () => {
        try {
          await this.enqueue(() => this.post(this.url('update'), { attributes: { [this.strings.attr_note]: note.value.trim() } }));
          const saved = this.querySelector('[data-vl-saved]');
          if (saved) {
            saved.textContent = this.t('saved');
            saved.classList.add('is-visible');
            clearTimeout(this.savedTimer);
            this.savedTimer = setTimeout(() => saved.classList.remove('is-visible'), 1800);
          }
        } catch (e) {
          /* wird beim Checkout ohnehin mitgesendet */
        }
      }, 600);
    }

    /* ---------- Zustelldatum ---------- */
    initEta() {
      const el = this.querySelector('[data-vl-eta-date]');
      if (!el) return;
      const days = Number(el.dataset.days);
      if (!days) return;
      const addWorkdays = (n) => {
        const d = new Date();
        let k = 0;
        while (k < n) {
          d.setDate(d.getDate() + 1);
          const w = d.getDay();
          if (w !== 0 && w !== 6) k++;
        }
        return d;
      };
      try {
        const f = new Intl.DateTimeFormat(this.locale, { weekday: 'short', day: 'numeric', month: 'short' });
        el.textContent = this.t('eta_by', { date: f.format(addWorkdays(days)) });
      } catch (e) {
        /* Fallback-Text bleibt */
      }
    }

    /* ---------- Mobile Leiste ---------- */
    initBar() {
      this.io?.disconnect();
      const bar = this.querySelector('[data-vl-bar]');
      const target = this.querySelector('[data-vl-checkout-main]');
      this.classList.remove('has-bar');
      document.documentElement.classList.remove('vl-ui-bar');
      if (!bar || !target || !('IntersectionObserver' in window)) return;

      this.io = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top > 0;
        bar.classList.toggle('is-visible', show);
        bar.inert = !show;
        bar.setAttribute('aria-hidden', String(!show));
        this.classList.toggle('has-bar', show);
        document.documentElement.classList.toggle('vl-ui-bar', show); // blendet Floating Contact mobil aus
      });
      this.io.observe(target);
    }

    /* ---------- Kasse ---------- */
    onCheckout(e, btn) {
      if (btn.classList.contains('is-leaving')) {
        e.preventDefault();
        return;
      }
      this.querySelectorAll('[data-vl-checkout]').forEach((b) => b.classList.add('is-leaving'));
      btn.setAttribute('aria-busy', 'true');

      // läuft noch eine Änderung? Erst abwarten, dann absenden
      if (this.pending > 0) {
        e.preventDefault();
        this.queue.then(() => {
          const form = document.getElementById('vl-cart-form');
          if (form?.requestSubmit) form.requestSubmit(btn);
          else form?.submit();
        });
      }
    }

    resetCheckout() {
      this.querySelectorAll('[data-vl-checkout]').forEach((b) => {
        b.classList.remove('is-leaving', 'is-pressed');
        b.removeAttribute('aria-busy');
      });
    }

    /* ---------- Reservierung ---------- */
    openReserve() {
      const d = this.querySelector('[data-vl-reserve]');
      if (!d || d.open) return;
      const items = this.querySelectorAll('[data-vl-item]');
      const meta = d.querySelector('[data-vl-res-meta]');
      const total = this.querySelector('[data-vl-total]')?.dataset.value || '';
      if (meta) meta.textContent = items.length === 1 ? this.t('item', { total }) : this.t('items', { count: items.length, total });
      this.resState('form');
      d.showModal();
      requestAnimationFrame(() => d.classList.add('is-open'));
    }

    closeReserve() {
      const d = this.querySelector('[data-vl-reserve]');
      if (!d || !d.open) return;
      d.classList.remove('is-open');
      setTimeout(() => d.close(), ms(320));
    }

    resState(state) {
      const d = this.querySelector('[data-vl-reserve]');
      d?.querySelectorAll('[data-vl-res-state]').forEach((el) => (el.hidden = el.dataset.vlResState !== state));
    }

    async submitReserve(form) {
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }
      const d = this.querySelector('[data-vl-reserve]');
      const btn = form.querySelector('[type="submit"]');
      btn.disabled = true;

      const items = [...this.querySelectorAll('[data-vl-item]')].map((li) => ({
        product_title: li.dataset.title,
        variant_id: Number(li.dataset.variantId),
        sku: li.dataset.sku,
        price: Number(li.dataset.price),
        product_url: li.dataset.url,
      }));
      const fd = new FormData(form);
      const payload = {
        type: 'reservation',
        source: 'cart',
        submitted_at: new Date().toISOString(),
        name: String(fd.get('name') || '').trim(),
        email: String(fd.get('email') || '').trim(),
        phone: String(fd.get('phone') || '').trim(),
        preferred_date: '',
        message: String(fd.get('message') || '').trim(),
        product: items[0] || null,
        items,
        cart_total: this.querySelector('[data-vl-total]')?.dataset.value || '',
        currency: this.dataset.currency,
        page_url: window.location.href,
      };

      try {
        const res = await fetch(d.dataset.webhook, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        form.reset();
        this.resState('success');
      } catch (err) {
        console.error('[VALOTERNA] Reservierung fehlgeschlagen:', err);
        this.resState('error');
      } finally {
        btn.disabled = false;
      }
    }
  }

  customElements.define('vl-cart', VlCart);
})();
